import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Zap, Package, TrendingUp, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { NeonButton, accentColor } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, estImages } from "@/lib/billing";

// FUTRA Credit Center — surfaced when a user is low/out of credits.
export default function CreditCenter({ onClose, onGoPlans }) {
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(null);
  const [ar, setAr] = useState(user?.auto_recharge || { enabled: false, threshold: 100, pack_id: null });
  const c = accentColor("turquoise");

  useEffect(() => { api.get("/packs").then(({ data }) => { setPacks(data); setAr((p) => ({ ...p, pack_id: p.pack_id || data[1]?.id || data[0]?.id })); }).catch(() => {}); }, []);

  const buy = async (pack) => {
    setLoading(pack.id);
    try { await startCheckout({ kind: "credits", pack_id: pack.id }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(null); }
  };

  const saveAuto = async (enabled) => {
    try {
      const { data } = await api.put("/payments/auto-recharge", { enabled, threshold: Number(ar.threshold) || 100, pack_id: ar.pack_id, monthly_cap: ar.monthly_cap ?? 50 });
      setAr(data); await refreshUser();
      toast.success(enabled ? "Auto Recharge enabled" : "Auto Recharge disabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={onClose} data-testid="credit-center">
      <div className="glass clip-corner border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" style={{ borderTop: `1px solid ${c.hex}` }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display font-bold text-2xl flex items-center gap-2"><Zap className="text-neon-turquoise" size={20} /> FUTRA Credit Center</h2>
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-neon-turquoise mt-1">
              {user?.credits <= 0 ? "You're out of credits — recharge to keep creating" : `${user?.credits?.toLocaleString()} credits remaining`}
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-neon-pink transition-colors" data-testid="close-credit-center"><X size={20} /></button>
        </div>

        {user?.credits <= 0 && (
          <div className="flex items-center gap-3 border border-neon-pink/40 bg-neon-pink/5 p-3 mb-5 font-mono-tech text-[11px] text-neon-pink">
            <AlertTriangle size={15} className="shrink-0" /> Purchased credits never expire and roll over indefinitely.
          </div>
        )}

        <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-1.5 mb-3"><Package size={12} /> Buy a Credit Pack</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {packs.map((p) => (
            <button key={p.id} onClick={() => buy(p)} disabled={loading === p.id} data-testid={`credit-pack-${p.id}`}
              className="border border-white/12 hover:border-neon-turquoise p-4 text-left transition-colors group">
              <div className="font-display font-bold text-lg text-neon-turquoise">+{p.credits.toLocaleString()}</div>
              <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-wider">credits · {p.multiplier}</div>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-display font-bold">${p.price}</span>
                {loading === p.id ? <Loader2 size={14} className="animate-spin text-neon-turquoise" /> : <TrendingUp size={13} className="text-white/30 group-hover:text-neon-turquoise" />}
              </div>
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="border border-white/10 p-4">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neon-purple mb-2">Upgrade Plan</p>
            <p className="font-mono-tech text-[11px] text-white/50 mb-3">Get more monthly credits and premium tools.</p>
            <NeonButton accent="purple" onClick={() => { onClose(); onGoPlans && onGoPlans(); }} data-testid="cc-upgrade-btn">See Plans</NeonButton>
          </div>
          <div className="border border-white/10 p-4" data-testid="auto-recharge-box">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neon-green mb-2 flex items-center gap-1.5"><RefreshCw size={12} /> Auto Recharge</p>
            <p className="font-mono-tech text-[11px] text-white/50 mb-3">When credits drop below a threshold, we'll prompt a one-click top-up.</p>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono-tech text-[10px] text-white/40 uppercase">Below</span>
              <input type="number" value={ar.threshold} onChange={(e) => setAr((p) => ({ ...p, threshold: e.target.value }))} data-testid="ar-threshold"
                className="w-20 bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-xs" />
              <select value={ar.pack_id || ""} onChange={(e) => setAr((p) => ({ ...p, pack_id: e.target.value }))} data-testid="ar-pack"
                className="flex-1 bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-xs">
                {packs.map((p) => <option key={p.id} value={p.id} className="bg-[#0d0d0f]">+{p.credits} (${p.price})</option>)}
              </select>
            </div>
            <NeonButton accent={ar.enabled ? "pink" : "green"} onClick={() => saveAuto(!ar.enabled)} data-testid="ar-toggle-btn">
              {ar.enabled ? "Disable Auto Recharge" : "Enable Auto Recharge"}
            </NeonButton>
          </div>
        </div>
        <p className="font-mono-tech text-[9px] text-white/25 mt-4">≈ {estImages(user?.credits)} generations remaining. Secure checkout via Stripe.</p>
      </div>
    </div>
  );
}
